<template>
  <NuxtLayout :name="layout">
    <client-only>
      <user-account-section class="signup" :class="{ desktop: isDesktop }">
        <template #header>
          <translate tag="h1"> Create an account </translate>
        </template>

        <div>
          <translate tag="p"
            >View future bookings, save favourites, add reviews, get directions,
            book again and find out how to cancel or change your
            booking.</translate
          >
          <hr />
          <signup-form />
        </div>
      </user-account-section>
    </client-only>
  </NuxtLayout>
</template>

<script setup lang="ts">
import { getAuthService } from '~/apps/auth/service'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const { $isDesktop } = useNuxtApp()

// Get the auth machine
const authMachine = getAuthService()
const authState = ref(authMachine.state)

// Subscribe to auth state changes
authMachine.onTransition((state) => {
  authState.value = state
})

// Check if user is logged in and redirect if so (RequireGuestMixin behavior)
const isLoggedIn = computed(() => {
  return (
    !process.server && authState.value && authState.value.matches('loggedIn')
  )
})

onMounted(async () => {
  // Wait for auth initialization
  if (authState.value?.matches('initialising')) {
    await new Promise((resolve) => {
      authMachine.onTransition((state) => {
        if (!state.matches('initialising')) {
          resolve(state)
        }
      })
    })
  }

  // Redirect if logged in
  if (isLoggedIn.value) {
    await router.push('/')
  }
})

const isDesktop = computed(() => $isDesktop)
const layout = computed(() => {
  return isDesktop.value ? 'no-navbar' : 'user-account'
})

definePageMeta({
  layout: false,
})

useHead({
  title: t('Sign up for a Pitchup.com account') as string,
  meta: [
    {
      hid: 'description',
      name: 'description',
      property: 'og:description',
      content: t(
        'View future bookings, save favourites, add reviews, get directions, book again and find out how to cancel or change your booking.',
      ) as string,
    },
  ],
  link: [
    {
      hid: 'i18n-can',
      rel: 'canonical',
      href: `https://${useRuntimeConfig().public.envUrl}${route.path}`,
    },
  ],
})
</script>
<style scoped>
h1 {
  font-size: 1em;
}

p {
  padding: 1em 4vw;
  font-size: var(--body);
  background: var(--colour-page-background);
  color: var(--colour-dark-blue);
}

hr {
  background-color: #a2afbb;
}

/* desktop & tablet */
.signup.desktop p {
  background-color: transparent;
  padding: 0 0 2em;
  font-size: inherit;
}

.signup.desktop hr {
  background-color: transparent;
}

.signup.desktop :deep(.nav-bar) {
  background-color: transparent;
  height: auto;
  padding: 1.5em;
}

.signup.desktop :deep(nav .col) {
  padding: 0;
}

.signup.desktop h1 {
  font-size: 1.5em;
  padding: 0.5em 0;
  margin: 0;
  text-align: center;
  color: var(--colour-dark-blue);
}

.signup.desktop :deep(.page-title) {
  margin: auto;
}

.signup.desktop :deep(.back) {
  display: none;
}

.signup.desktop {
  align-items: start;
  display: grid;
  margin: 0 auto 6em;
  max-width: var(--legacy-page-width-max);
  width: 32em;
}
</style>
